import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export type FindingSeverity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'INFO';
export type FindingState = 'OPEN' | 'RESOLVED';
export type FindingSource = 'event' | 'poll' | 'reconciliation';
export type MonitoringResourceType = 'LAMBDA' | 'GLUE' | 'PIPELINE' | 'STEP_FUNCTIONS' | 'ECS_SERVICE' | 'RDS_INSTANCE' | 'API_GATEWAY' | 'EC2_INSTANCE' | 'COGNITO' | 'APPSYNC' | 'DYNAMODB' | 'SQS' | 'EVENTBRIDGE' | 'ALB' | 'SNS' | 'CLOUDFRONT';
export type FindingType =
  | 'LAMBDA_ERROR_RATE_HIGH'
  | 'LAMBDA_THROTTLE_RATE_HIGH'
  | 'LAMBDA_FUNCTION_UNHEALTHY'
  | 'GLUE_JOB_FAILED'
  | 'GLUE_JOB_TIMEOUT'
  | 'PIPELINE_EXECUTION_FAILED'
  | 'PIPELINE_EXECUTION_CANCELED'
  | 'STEP_FUNCTIONS_EXECUTION_FAILED'
  | 'STEP_FUNCTIONS_EXECUTION_TIMED_OUT'
  | 'STEP_FUNCTIONS_EXECUTION_ABORTED'
  | 'STEP_FUNCTIONS_STATE_MACHINE_UNHEALTHY'
  | 'ECS_SERVICE_TASK_COUNT_LOW'
  | 'ECS_SERVICE_DEPLOYMENT_FAILED'
  | 'ECS_SERVICE_UNHEALTHY'
  | 'RDS_INSTANCE_UNAVAILABLE'
  | 'RDS_INSTANCE_STORAGE_LOW'
  | 'RDS_INSTANCE_STORAGE_CRITICAL'
  | 'RDS_INSTANCE_UNHEALTHY'
  | 'API_GATEWAY_5XX_ERROR_RATE_HIGH'
  | 'API_GATEWAY_4XX_ERROR_RATE_HIGH'
  | 'API_GATEWAY_LATENCY_HIGH'
  | 'API_GATEWAY_INTEGRATION_LATENCY_HIGH'
  | 'EC2_INSTANCE_STOPPED'
  | 'EC2_INSTANCE_TERMINATED'
  | 'EC2_INSTANCE_STATUS_CHECK_FAILED'
  | 'EC2_INSTANCE_CPU_HIGH'
  | 'COGNITO_SIGN_IN_FAILURES_HIGH'
  | 'COGNITO_THROTTLING_DETECTED'
  | 'APPSYNC_5XX_ERROR_RATE_HIGH'
  | 'APPSYNC_4XX_ERROR_RATE_HIGH'
  | 'APPSYNC_LATENCY_HIGH'
  | 'DYNAMODB_THROTTLING_DETECTED'
  | 'DYNAMODB_READ_THROTTLING_DETECTED'
  | 'DYNAMODB_WRITE_THROTTLING_DETECTED'
  | 'DYNAMODB_SYSTEM_ERRORS_DETECTED'
  | 'RESOURCE_UNREACHABLE';

export interface MonitoringFinding {
  findingId:    string;
  orgId:        string | null;
  ownerId:      string;
  configId:     string;
  serviceType:  MonitoringResourceType;
  resourceId:   string;
  region?:      string;   // region scope of the monitored resource ('global' for account-scoped services)
  severity:     FindingSeverity;
  findingState: FindingState;
  findingType:  FindingType;
  title:        string;
  summary:      string;
  fingerprint:  string;
  source:       FindingSource;
  observedAt:   string;
  createdAt:    string;
  resolvedAt?:  string;
}

@Injectable({ providedIn: 'root' })
export class MonitoringService {
  private readonly base = `${environment.apiUrl}/api/v1/monitoring`;

  constructor(private readonly http: HttpClient) {}

  getFindings(opts: { onlyOpen?: boolean } = {}): Observable<MonitoringFinding[]> {
    const params: Record<string, string> = {};
    if (opts.onlyOpen) params['onlyOpen'] = 'true';
    return this.http
      .get<{ findings: MonitoringFinding[] }>(`${this.base}/findings`, { params })
      .pipe(map(r => r.findings));
  }
}
